"use client";

import type { RefObject } from "react";
import type { PlacePredictionsMeta } from "~www/components/LocationSearchForm/GoogleLocationAutocomplete";
import type { UtilityBarOffice } from "~www/types/utility-bar";

import { cn } from "@homeinstead/ui";
import { useMutation } from "@tanstack/react-query";
import { forwardRef, useCallback, useRef, useState } from "react";
import LocationSearchForm from "~www/components/LocationSearchForm/LocationSearchForm";
import { searchNearestOfficeCanonicalPath } from "~www/lib/locations/search-nearest-office-canonical-path";
import OfficeDetails from "./OfficeDetails";

type OfficeLocationSearchProps = React.HTMLAttributes<HTMLDialogElement> & {
  office?: UtilityBarOffice;
  officePageURL?: string;
  onSelectOffice: (canonicalPath: string) => void;
};

/**
 * Displays office location search form and office details (when an office is provided).
 * @param props.office - The office to display the search form for.
 * @param props.officePageURL - The URL to the office page.
 * @param props.onSelectOffice - The function to call when an office is selected, receives the canonical URL path.
 * @param props.className - The className to apply to the component.
 * @param ref - The ref to the component.
 */
function OfficeLocationSearch(
  {
    office,
    officePageURL,
    onSelectOffice,
    className,
  }: OfficeLocationSearchProps,
  ref: RefObject<HTMLDialogElement>,
) {
  const [place, setPlace] = useState("");
  const [noResultsMessage, setNoResultsMessage] = useState("");
  const predictionsMeta = useRef<PlacePredictionsMeta>({
    count: 0,
    loading: false,
  });

  const { mutate: searchPlaces, isPending } = useMutation({
    mutationFn: async () => searchNearestOfficeCanonicalPath(place),
    onError: () => {
      setNoResultsMessage(
        "An error occurred while searching for locations. Please try again later.",
      );
    },
    onSuccess: (canonicalPath) => {
      if (!canonicalPath) {
        setNoResultsMessage(
          "No locations found for your search. Please try a different location.",
        );
        return;
      }

      setNoResultsMessage("");
      onSelectOffice(canonicalPath);
    },
  });

  function handleSubmit() {
    if (!place.trim()) {
      setNoResultsMessage(
        predictionsMeta.current.count > 0
          ? "Please select a location from the suggestions list."
          : "No location found. Try adding details like postal code or state name.",
      );
      return;
    }

    searchPlaces();
  }

  function handleSelectPlace(place: string) {
    setPlace(place);
    setNoResultsMessage("");
  }

  function handleInputChange() {
    handleSelectPlace("");
  }

  const handlePredictionsMetaChange = useCallback(
    (meta: PlacePredictionsMeta) => {
      predictionsMeta.current = meta;
    },
    [],
  );

  return (
    <dialog
      className={cn(
        "fixed z-50 m-0 flex w-full max-w-[100vw] flex-col overflow-y-auto rounded-lg bg-white p-0 text-left text-new-beginnings-base shadow-xl",
        "md:inset-auto md:w-[600px] md:max-w-[600px] md:p-0",
        className,
      )}
      ref={ref}
    >
      <OfficeDetails office={office} officePageURL={officePageURL} />

      <LocationSearchForm
        className={cn("p-4 md:p-8", noResultsMessage && "pb-2 md:pb-2")}
        disableOnEmptyValue
        fixedDropdown
        label={office ? "Search for another location" : "Search for a location"}
        loading={isPending}
        onInputChange={handleInputChange}
        onPredictionsMetaChange={handlePredictionsMetaChange}
        onSelect={handleSelectPlace}
        onSubmit={handleSubmit}
        showCurrentLocation={false}
        slotClassNames={{ buttonWrapper: "mt-1" }}
      />

      {noResultsMessage ? (
        <p className="px-4 pb-4 text-red-600 text-sm md:px-8 md:pb-8">
          {noResultsMessage}
        </p>
      ) : null}
    </dialog>
  );
}

OfficeLocationSearch.displayName = "OfficeLocationSearch";

export default forwardRef<HTMLDialogElement, OfficeLocationSearchProps>(
  OfficeLocationSearch,
);
